<template>
  <div class="tin-root">
    <div class="ambient"></div>
    <div class="ambient-floor"></div>
    <div class="grain"></div>
    <div class="frame"></div>
    <div class="frame-inner"></div>

    <div class="page">

      <!-- ── SEARCH ── -->
      <div class="search-wrap">
        <div class="search-box">
          <span class="search-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </span>
          <input
            v-model="searchText"
            class="search-input"
            type="text"
            placeholder="Search chapters by city…"
          />
          <div class="search-bar-line"></div>
          <button v-if="searchText" class="search-clear" @click="searchText = ''">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>

      <!-- ── SECTION HEADER ── -->
      <div class="section-hd">
        <div class="section-rule l"></div>
        <span class="section-eyebrow">City Chapters</span>
        <div class="section-rule"></div>
      </div>
      <h2 class="section-title">Your <em>Chapters</em></h2>
      <p class="section-lede">
        Every city where members have recommended a place, or asked for an official arrangement, has a home base here —
        intelligence and conversation, organized by city instead of by interest.
      </p>

      <!-- ── LOADING ── -->
      <div v-if="isLoading" class="loading-row">
        <div class="loading-seal">
          <div class="seal-ring"></div>
          <div class="seal-ring-dashed"></div>
          <div class="seal-diamond"></div>
        </div>
        <p class="loading-text">Gathering your chapters…</p>
      </div>

      <!-- ── GRID ── -->
      <div v-else-if="filteredChapters.length" class="cards-grid">
        <div
          v-for="chapter in filteredChapters"
          :key="chapter.city"
          class="group-card"
          @click="viewChapter(chapter.city)"
        >
          <div class="card-img-wrap">
            <img
              v-if="chapter.imageURL"
              :src="chapter.imageURL"
              :alt="chapter.city"
              class="card-img"
            />
            <div v-else class="card-img-placeholder">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><path d="M12 21s-7-6.2-7-11.5A7 7 0 0119 9.5C19 14.8 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.3"/></svg>
            </div>
            <span v-if="chapter.isMember" class="card-badge">Joined</span>
          </div>

          <div class="card-body">
            <p class="card-name">{{ chapter.city }}</p>
            <p v-if="chapter.topRequested" class="card-desc">
              Most wanted: {{ chapter.topRequested.businessName }} · {{ chapter.topRequested.count }} {{ chapter.topRequested.count === 1 ? 'request' : 'requests' }}
            </p>
            <p v-else class="card-desc">No requested arrangements yet</p>

            <div class="card-stats">
              <span class="card-stat">{{ chapter.recommendationCount }} {{ chapter.recommendationCount === 1 ? 'recommendation' : 'recommendations' }}</span>
              <span class="card-stat-divider"></span>
              <span v-if="chapter.travelerCount" class="card-stat">{{ chapter.travelerCount }} traveling now</span>
              <span v-else class="card-stat">{{ chapter.contributorCount }} {{ chapter.contributorCount === 1 ? 'member' : 'members' }}</span>
            </div>
          </div>

          <div class="card-actions">
            <button class="card-btn" @click.stop="viewChapter(chapter.city)">
              <div class="card-btn-tick"></div>
              <span>Enter Chapter</span>
              <div class="card-btn-tick"></div>
            </button>
          </div>
        </div>
      </div>

      <!-- ── EMPTY ── -->
      <div v-else class="empty-state">
        <div class="empty-seal">
          <div class="seal-ring"></div>
          <div class="seal-ring-dashed"></div>
          <div class="seal-diamond"></div>
        </div>
        <p class="empty-title">No chapters found</p>
        <p class="empty-sub">
          {{ searchText ? 'Try a different city.' : 'Start one below for a city not yet on the network.' }}
        </p>
      </div>

    </div>

    <!-- ── FAB ── -->
    <button class="fab" @click="openStartChapter">+</button>

    <!-- ── START A CHAPTER ── -->
    <div v-if="showStartChapter" class="overlay" @click.self="showStartChapter = false">
      <div class="start-panel">
        <div class="section-hd">
          <div class="section-rule l"></div>
          <span class="section-eyebrow">New Chapter</span>
          <div class="section-rule"></div>
        </div>
        <h3 class="start-title">Start a <em>Chapter</em></h3>
        <p class="start-sub">Not every city has recommendations yet. Start its chapter now, and be the first member in it.</p>

        <div class="city-field-wrap">
          <input
            v-model="newChapterCity"
            class="start-input"
            type="text"
            placeholder="e.g. Miami"
            @keyup.enter="createChapter"
          />
          <div v-if="newChapterCitySuggestions.length" class="city-suggestions">
            <p class="city-suggestions-label">Already a chapter — tap to use it</p>
            <button
              v-for="match in newChapterCitySuggestions"
              :key="match"
              class="city-suggestion-item"
              @click="selectNewChapterCitySuggestion(match)"
            >{{ match }}</button>
          </div>
        </div>

        <div class="start-actions">
          <button class="start-btn-primary" :disabled="!newChapterCity.trim() || isCreating" @click="createChapter">
            <div class="start-btn-tick"></div>
            <span>Start Chapter</span>
            <div class="start-btn-tick"></div>
          </button>
          <button class="start-btn-text" @click="showStartChapter = false">Cancel</button>
        </div>
      </div>
    </div>

    <!-- ── BOTTOM NAV ── -->
    <nav class="bottom-nav">
      <div class="bottom-nav-glow"></div>
      <button class="nav-item" @click="$router.push('/home')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        <span class="nav-label">Home</span>
      </button>
      <button class="nav-item" @click="$router.push('/search')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <span class="nav-label">Search</span>
      </button>
      <button class="nav-item" @click="$router.push('/explore')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>
        <span class="nav-label">Explore</span>
      </button>
      <button class="nav-item" @click="$router.push('/messages')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
        <span class="nav-label">Messages</span>
      </button>
      <button class="nav-item active" @click="$router.push('/groups')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
        <span class="nav-label">Groups</span>
        <div class="nav-dot"></div>
      </button>
      <button class="nav-item" @click="$router.push('/member-settings')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        <span class="nav-label">Profile</span>
      </button>
    </nav>
  </div>
</template>

<script>
import { auth, db } from "@/firebase";
import { collection, getDocs, query, where, addDoc, serverTimestamp } from "firebase/firestore";
import { normalizeCity, findMatchingCities } from "@/utils/city";

export default {
  name: "GroupsView",

  data() {
    return {
      chapters: [],
      searchText: "",
      isLoading: true,
      userUID: null,
      showStartChapter: false,
      newChapterCity: "",
      isCreating: false,
    };
  },

  computed: {
    filteredChapters() {
      const q = this.searchText.trim().toLowerCase();
      if (!q) return this.chapters;
      return this.chapters.filter((c) => c.city.toLowerCase().includes(q));
    },

    newChapterCitySuggestions() {
      if (!this.newChapterCity.trim()) return [];
      const existingCityNames = this.chapters.map((c) => c.city);
      return findMatchingCities(this.newChapterCity, existingCityNames)
        .filter((c) => c.toLowerCase() !== this.newChapterCity.trim().toLowerCase())
        .slice(0, 4);
    },
  },

  async mounted() {
    await this.fetchChapters();
  },

  methods: {
    async fetchChapters() {
      this.isLoading = true;
      try {
        this.userUID = auth.currentUser?.uid || null;

        // Public recommendations drive which cities are active
        const exploreSnap = await getDocs(
          query(collection(db, "explore"), where("isPublic", "==", true))
        );
        const posts = exploreSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // Existing chapter docs carry membership
        const groupsSnap = await getDocs(collection(db, "groups"));
        const chapterDocs = groupsSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((g) => g.city);

        // Group by NORMALIZED city so spelling variants (e.g. "Boston" and
        // "Boston, MA") merge into one chapter instead of fragmenting.
        const byNormalizedCity = new Map();

        const getEntry = (rawCity) => {
          const key = normalizeCity(rawCity);
          if (!byNormalizedCity.has(key)) {
            byNormalizedCity.set(key, {
              chapterIds: [],
              members: [],
              posts: [],
              imageURL: null,
              variantCounts: new Map(), // raw city string -> weight, used to pick a display name
            });
          }
          return byNormalizedCity.get(key);
        };

        const bumpVariant = (entry, rawCity, weight) => {
          entry.variantCounts.set(rawCity, (entry.variantCounts.get(rawCity) || 0) + weight);
        };

        posts.forEach((post) => {
          if (!post.city) return;
          const entry = getEntry(post.city);
          entry.posts.push(post);
          bumpVariant(entry, post.city, 1);
        });

        chapterDocs.forEach((groupDoc) => {
          const entry = getEntry(groupDoc.city);
          entry.chapterIds.push(groupDoc.id);
          const members = Array.isArray(groupDoc.members) ? groupDoc.members : [];
          entry.members = [...new Set([...entry.members, ...members])];
          if (!entry.imageURL && groupDoc.imageURL) entry.imageURL = groupDoc.imageURL;
          // A claimed chapter doc counts for more than a bare mention in a post
          bumpVariant(entry, groupDoc.city, 3);
        });

        this.chapters = [...byNormalizedCity.values()]
          .map((entry) => {
            // Pick whichever raw spelling has the strongest signal as the display name;
            // ties broken by shortest string (avoids ", MA"-style suffixes winning).
            const displayCity = [...entry.variantCounts.entries()]
              .sort((a, b) => b[1] - a[1] || a[0].length - b[0].length)[0][0];

            const recommendationPosts = entry.posts.filter((p) => (p.postType || "recommendation") !== "travel_plan");
            const travelPlanPosts = entry.posts.filter((p) => p.postType === "travel_plan");

            const contributorSet = new Set(entry.posts.map((p) => p.uid).filter(Boolean));
            entry.members.forEach((uid) => contributorSet.add(uid));

            const byPlace = new Map();
            recommendationPosts.forEach((p) => {
              if (!p.businessName) return;
              const arrangementCount = Array.isArray(p.arrangementInterest) ? p.arrangementInterest.length : 0;
              const key = p.businessName.toLowerCase();
              const existing = byPlace.get(key);
              byPlace.set(key, {
                businessName: p.businessName,
                count: (existing ? existing.count : 0) + arrangementCount,
              });
            });
            const topRequested = [...byPlace.values()].sort((a, b) => b.count - a.count)[0] || null;

            return {
              city: displayCity,
              chapterId: entry.chapterIds[0] || null,
              imageURL: entry.imageURL || null,
              memberCount: entry.members.length,
              recommendationCount: recommendationPosts.length,
              travelerCount: new Set(travelPlanPosts.map((p) => p.uid).filter(Boolean)).size,
              contributorCount: contributorSet.size,
              isMember: this.userUID ? entry.members.includes(this.userUID) : false,
              topRequested: topRequested && topRequested.count > 0 ? topRequested : null,
            };
          })
          .sort((a, b) => (b.recommendationCount + b.travelerCount + b.memberCount) - (a.recommendationCount + a.travelerCount + a.memberCount));
      } catch (error) {
        console.error("Error fetching chapters:", error);
      } finally {
        this.isLoading = false;
      }
    },

    viewChapter(city) {
      this.$router.push(`/groups/${encodeURIComponent(city)}`);
    },

    openStartChapter() {
      this.newChapterCity = "";
      this.showStartChapter = true;
    },

    async createChapter() {
      const city = this.newChapterCity.trim();
      if (!city || this.isCreating) return;

      const existing = this.chapters.find((c) => normalizeCity(c.city) === normalizeCity(city));
      if (existing) {
        this.showStartChapter = false;
        this.viewChapter(existing.city);
        return;
      }

      this.isCreating = true;
      try {
        const uid = auth.currentUser?.uid;
        await addDoc(collection(db, "groups"), {
          city,
          members: uid ? [uid] : [],
          createdAt: serverTimestamp(),
        });
        this.showStartChapter = false;
        this.viewChapter(city);
      } catch (error) {
        console.error("Error starting chapter:", error);
      } finally {
        this.isCreating = false;
      }
    },

    selectNewChapterCitySuggestion(match) {
      this.newChapterCity = match;
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

.tin-root { position: relative; min-height: 100vh; width: 100%; background: #070708; font-family: 'Montserrat', sans-serif; }

.ambient { position: fixed; top: -300px; left: 50%; transform: translateX(-50%); width: 1000px; height: 800px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 10%, rgba(210,175,100,0.09) 0%, rgba(210,175,100,0.03) 30%, transparent 65%); }
.ambient-floor { position: fixed; bottom: -200px; left: 50%; transform: translateX(-50%); width: 700px; height: 400px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 100%, rgba(210,175,100,0.03) 0%, transparent 60%); }
.grain { position: fixed; inset: 0; z-index: 1; pointer-events: none; opacity: 0.5; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.12'/%3E%3C/svg%3E"); background-size: 180px 180px; }
.frame { position: fixed; inset: 20px; border: 1px solid rgba(210,175,100,0.07); z-index: 2; pointer-events: none; }
.frame::before, .frame::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.frame::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner { position: fixed; inset: 20px; z-index: 2; pointer-events: none; }
.frame-inner::before, .frame-inner::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame-inner::before { top: -1px; right: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner::after  { bottom: -1px; left: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }

.page { position: relative; z-index: 10; max-width: 1100px; margin: 0 auto; padding: 48px 48px 140px; animation: fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) both; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

/* ── SEARCH ── */
.search-wrap { display: flex; justify-content: center; margin-bottom: 44px; }
.search-box { position: relative; width: 100%; max-width: 600px; }
.search-icon { position: absolute; left: 0; top: 50%; transform: translateY(-50%); color: rgba(210,175,100,0.4); pointer-events: none; display: flex; }
.search-icon svg { width: 18px; height: 18px; }
.search-input { width: 100%; background: transparent; border: none; border-bottom: 1px solid rgba(237,232,223,0.15); padding: 14px 36px 14px 32px; color: #edeae2; font-family: 'Montserrat', sans-serif; font-size: 16px; font-weight: 300; outline: none; caret-color: rgba(210,175,100,0.8); transition: border-color 0.3s; letter-spacing: 0.3px; }
.search-input::placeholder { color: rgba(237,232,223,0.2); font-style: italic; }
.search-input:focus { border-bottom-color: rgba(210,175,100,0.4); }
.search-bar-line { position: absolute; bottom: 0; left: 0; height: 1px; width: 0; background: linear-gradient(90deg, rgba(210,175,100,0.8), rgba(210,175,100,0.2)); transition: width 0.5s cubic-bezier(0.25,0.46,0.45,0.94); pointer-events: none; }
.search-input:focus ~ .search-bar-line { width: 100%; }
.search-clear { position: absolute; right: 0; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: rgba(237,232,223,0.25); display: flex; align-items: center; transition: color 0.2s; padding: 4px; }
.search-clear:hover { color: rgba(210,175,100,0.6); }
.search-clear svg { width: 14px; height: 14px; }

/* ── SECTION HEADER ── */
.section-hd { display: flex; align-items: center; gap: 20px; margin-bottom: 20px; }
.section-rule { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(210,175,100,0.18), transparent); }
.section-rule.l { background: linear-gradient(90deg, transparent, rgba(210,175,100,0.18)); }
.section-eyebrow { font-size: 9px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.45); text-transform: uppercase; white-space: nowrap; }
.section-title { font-family: 'Cormorant', serif; font-size: 40px; font-weight: 300; color: #edeae2; letter-spacing: -0.5px; margin-bottom: 14px; line-height: 1; }
.section-title em { font-style: italic; color: rgba(210,175,100,0.8); font-weight: 300; }
.section-lede { font-size: 13px; font-weight: 300; line-height: 1.75; color: rgba(237,232,223,0.4); max-width: 640px; margin-bottom: 32px; }

/* ── LOADING ── */
.loading-row { display: flex; flex-direction: column; align-items: center; gap: 20px; padding: 80px 0; }
.loading-seal, .empty-seal { position: relative; width: 48px; height: 48px; display: flex; align-items: center; justify-content: center; margin-bottom: 8px; }
.seal-ring { position: absolute; inset: 0; border-radius: 50%; border: 1px solid rgba(210,175,100,0.2); animation: spinSlow 6s linear infinite; }
.seal-ring-dashed { position: absolute; inset: 5px; border-radius: 50%; border: 1px dashed rgba(210,175,100,0.15); animation: spinSlow 4s linear infinite reverse; }
@keyframes spinSlow { to { transform: rotate(360deg); } }
.seal-diamond { width: 12px; height: 12px; border: 1px solid rgba(210,175,100,0.6); transform: rotate(45deg); position: relative; z-index: 1; }
.seal-diamond::after { content: ''; position: absolute; inset: 2px; background: rgba(210,175,100,0.3); }
.loading-text { font-size: 10px; font-weight: 500; letter-spacing: 3px; color: rgba(237,232,223,0.2); text-transform: uppercase; }

/* ── CARDS GRID ── */
.cards-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
.group-card { border: 1px solid rgba(210,175,100,0.1); background: rgba(210,175,100,0.02); cursor: pointer; transition: border-color 0.3s; position: relative; overflow: hidden; }
.group-card::before, .group-card::after { content: ''; position: absolute; width: 14px; height: 14px; z-index: 3; }
.group-card::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.35); border-left: 1px solid rgba(210,175,100,0.35); }
.group-card::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.35); border-right: 1px solid rgba(210,175,100,0.35); }
.group-card:hover { border-color: rgba(210,175,100,0.3); }

.card-img-wrap { position: relative; }
.card-img { width: 100%; height: 140px; object-fit: cover; object-position: center; display: block; filter: brightness(0.8) saturate(0.75); }
.card-img-placeholder { width: 100%; height: 140px; background: rgba(210,175,100,0.04); display: flex; align-items: center; justify-content: center; color: rgba(210,175,100,0.25); }
.card-img-placeholder svg { width: 40px; height: 40px; }
.card-badge { position: absolute; top: 12px; right: 12px; font-size: 8px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase; padding: 4px 10px; border: 1px solid rgba(210,175,100,0.35); color: rgba(210,175,100,0.7); background: rgba(7,7,8,0.75); }

.card-body { padding: 20px 22px 0; }
.card-name { font-family: 'Cormorant', serif; font-size: 26px; font-weight: 400; color: #edeae2; letter-spacing: -0.2px; margin-bottom: 8px; line-height: 1.1; }
.card-desc { font-size: 10px; font-weight: 500; letter-spacing: 0.5px; color: rgba(210,175,100,0.45); text-transform: uppercase; margin-bottom: 14px; line-height: 1.5; }
.card-stats { display: flex; align-items: center; gap: 10px; margin-bottom: 18px; }
.card-stat { font-size: 9px; font-weight: 500; letter-spacing: 1px; color: rgba(237,232,223,0.25); }
.card-stat-divider { width: 3px; height: 3px; border-radius: 50%; background: rgba(210,175,100,0.3); }

.card-actions { display: flex; border-top: 1px solid rgba(210,175,100,0.08); }
.card-btn { flex: 1; background: transparent; border: none; padding: 15px 0; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; color: rgba(210,175,100,0.5); transition: color 0.2s; display: flex; align-items: center; justify-content: center; gap: 10px; position: relative; overflow: hidden; }
.card-btn::before { content: ''; position: absolute; inset: 0; background: rgba(210,175,100,0.05); transform: scaleX(0); transform-origin: left; transition: transform 0.35s cubic-bezier(0.25,0.46,0.45,0.94); }
.group-card:hover .card-btn::before { transform: scaleX(1); }
.card-btn:hover { color: rgba(210,175,100,0.8); }
.card-btn-tick { width: 14px; height: 1px; background: rgba(210,175,100,0.3); transition: width 0.3s; flex-shrink: 0; }
.group-card:hover .card-btn-tick { width: 20px; }

/* ── EMPTY STATE ── */
.empty-state { display: flex; flex-direction: column; align-items: center; padding: 80px 0; border: 1px solid rgba(210,175,100,0.08); }
.empty-title { font-family: 'Cormorant', serif; font-size: 26px; font-weight: 300; color: rgba(237,232,223,0.3); margin-bottom: 10px; }
.empty-sub { font-size: 11px; font-weight: 400; letter-spacing: 1.5px; color: rgba(237,232,223,0.15); text-transform: uppercase; }

/* ── FAB ── */
.fab { position: fixed; bottom: 88px; right: 28px; width: 48px; height: 48px; border: 1px solid rgba(210,175,100,0.4); background: rgba(7,7,8,0.92); color: rgba(210,175,100,0.7); display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 24px; font-weight: 300; z-index: 30; transition: border-color 0.2s, color 0.2s; line-height: 1; }
.fab:hover { border-color: rgba(210,175,100,0.8); color: rgba(210,175,100,1); }

/* ── START CHAPTER OVERLAY ── */
.overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.82); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 24px; }
.start-panel { background: #0a0a0b; border: 1px solid rgba(210,175,100,0.2); width: 100%; max-width: 420px; padding: 32px; position: relative; }
.start-panel::before, .start-panel::after { content: ''; position: absolute; width: 14px; height: 14px; }
.start-panel::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.start-panel::after { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.start-title { font-family: 'Cormorant', serif; font-size: 28px; font-weight: 300; color: #edeae2; margin-bottom: 10px; }
.start-title em { font-style: italic; color: rgba(210,175,100,0.8); }
.start-sub { font-size: 11.5px; font-weight: 300; line-height: 1.7; color: rgba(237,232,223,0.4); margin-bottom: 22px; }
.start-input { width: 100%; background: transparent; border: 1px solid rgba(210,175,100,0.25); padding: 13px 14px; color: #edeae2; font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 300; outline: none; caret-color: rgba(210,175,100,0.8); transition: border-color 0.2s; margin-bottom: 24px; }
.city-field-wrap { position: relative; }
.city-suggestions { position: absolute; top: calc(100% - 20px); left: 0; right: 0; z-index: 20; background: #0f0e0c; border: 1px solid rgba(210,175,100,0.3); padding: 6px 0; }
.city-suggestions-label { font-size: 8px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(210,175,100,0.4); padding: 6px 14px 4px; }
.city-suggestion-item { display: block; width: 100%; text-align: left; background: none; border: none; cursor: pointer; padding: 9px 14px; font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 400; color: rgba(237,232,223,0.75); transition: background 0.15s, color 0.15s; }
.city-suggestion-item:hover { background: rgba(210,175,100,0.1); color: rgba(210,175,100,0.95); }
.start-input::placeholder { color: rgba(237,232,223,0.2); font-style: italic; }
.start-input:focus { border-color: rgba(210,175,100,0.55); }
.start-actions { display: flex; align-items: center; gap: 14px; }
.start-btn-primary { display: flex; align-items: center; gap: 12px; font-family: 'Montserrat', sans-serif; font-size: 9.5px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(210,175,100,0.9); background: rgba(210,175,100,0.08); border: 1px solid rgba(210,175,100,0.5); padding: 13px 22px; cursor: pointer; transition: background 0.2s, border-color 0.2s, opacity 0.2s; }
.start-btn-primary:hover:not(:disabled) { background: rgba(210,175,100,0.16); border-color: rgba(210,175,100,0.75); }
.start-btn-primary:disabled { opacity: 0.35; cursor: not-allowed; }
.start-btn-tick { width: 14px; height: 1px; background: rgba(210,175,100,0.4); flex-shrink: 0; }
.start-btn-text { font-family: 'Montserrat', sans-serif; font-size: 9.5px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(237,232,223,0.3); background: none; border: none; cursor: pointer; padding: 13px 6px; transition: color 0.2s; }
.start-btn-text:hover { color: rgba(237,232,223,0.6); }

/* ── BOTTOM NAV ── */
.bottom-nav { position: fixed; bottom: 0; left: 0; right: 0; z-index: 20; background: #070708; border-top: 1px solid rgba(210,175,100,0.1); display: flex; align-items: center; justify-content: space-around; padding: 14px 0 18px; }
.bottom-nav-glow { position: absolute; top: -1px; left: 10%; right: 10%; height: 1px; background: linear-gradient(90deg, transparent, rgba(210,175,100,0.2), transparent); pointer-events: none; }
.nav-item { display: flex; flex-direction: column; align-items: center; gap: 5px; cursor: pointer; padding: 4px 20px; color: rgba(237,232,223,0.2); transition: color 0.2s; background: none; border: none; font-family: 'Montserrat', sans-serif; min-width: 60px; }
.nav-item.active { color: rgba(210,175,100,0.8); }
.nav-item:hover:not(.active) { color: rgba(237,232,223,0.45); }
.nav-item svg { width: 18px; height: 18px; }
.nav-label { font-size: 9px; font-weight: 500; letter-spacing: 1.5px; text-transform: uppercase; }
.nav-dot { width: 3px; height: 3px; border-radius: 50%; background: rgba(210,175,100,0.7); margin-top: 1px; }

/* ── RESPONSIVE ── */
@media (max-width: 900px) { .cards-grid { grid-template-columns: repeat(2, 1fr); } .page { padding: 40px 28px 120px; } }
@media (max-width: 600px) { .cards-grid { grid-template-columns: 1fr; } .page { padding: 32px 20px 120px; } .frame, .frame-inner { inset: 12px; } .section-title { font-size: 32px; } }
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } }
</style>